import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchPointExchangeRates,
  addPointExchangeRate,
  fetchCurrentRate,
  clearError as clearPointError,
} from "../../redux/slices/pointExchangeRateSlice";
import { Modal, Button, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import { LuCoins } from "react-icons/lu";
import api from "../../api/axios";

const PointExchangeRatePage = () => {
  const dispatch = useDispatch();
  
  const { 
    list: pointRateList, 
    currentRate, 
    error: pointRateError 
  } = useSelector((state) => state.pointExchangeRates);

  const [showPointModal, setShowPointModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [pointFormData, setPointFormData] = useState({
    company_id: "",
    bdt_per_point: "",
    effective_from: new Date().toISOString().slice(0, 16),
    notes: "",
  });

  useEffect(() => {
    api
      .get("/api/companies")
      .then((response) => setCompanies(response.data || []))
      .catch(() => toast.error("Failed to load companies."));
  }, []);

  useEffect(() => {
    const companyId = selectedCompanyId || null;
    dispatch(fetchPointExchangeRates(companyId));
    dispatch(fetchCurrentRate(companyId));
  }, [dispatch, selectedCompanyId]);

  useEffect(() => {
    if (pointRateError) {
      toast.error(pointRateError);
      dispatch(clearPointError());
    }
  }, [pointRateError, dispatch]);

  const openAddPointModal = () => {
    setPointFormData({
      company_id: selectedCompanyId,
      bdt_per_point: "",
      effective_from: new Date().toISOString().slice(0, 16),
      notes: "",
    });
    setShowPointModal(true);
  };

  const handlePointInputChange = (e) => {
    const { name, value } = e.target;
    setPointFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePointSubmit = async (e) => {
    e.preventDefault();
    if (!pointFormData.bdt_per_point || !pointFormData.effective_from) {
      toast.error("Please fill in all required fields!");
      return;
    }
    try {
      await dispatch(addPointExchangeRate({
        ...pointFormData,
        company_id: pointFormData.company_id || null,
      })).unwrap();
      toast.success("Point exchange rate added successfully!");
      setShowPointModal(false);
      const companyId = selectedCompanyId || null;
      dispatch(fetchPointExchangeRates(companyId));
      dispatch(fetchCurrentRate(companyId));
    } catch (err) {
      toast.error(err?.message || "Operation failed!");
    }
  };

  const getRateStatus = (rate) => {
    if (!currentRate) return "Past";
    if (rate.id === currentRate.id) return "Active";
    if (new Date(rate.effective_from) > new Date()) return "Scheduled";
    return "Past";
  };

  return (
    <div className="container-fluid">
      <div className="wrapper">
        <div className="container mt-5">
          <div className="d-flex align-items-center justify-content-center gap-3 mb-4">
            <LuCoins size={32} className="text-primary" />
            <h3 className="text-center mb-0 text-primary fw-bold">
              Point Exchange Rate Settings
            </h3>
          </div>

          <div className="p-4 bg-white rounded shadow-sm border">
            <div className="mb-4 d-flex justify-content-between align-items-center border-bottom pb-3">
              <div>
                <h5 className="mb-1 fw-bold">BDT to Points Ratio</h5>
                <p className="text-muted small mb-0">Manage the conversion rate from BDT to system points.</p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select"
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  aria-label="Filter rates by company"
                >
                  <option value="">Global (all companies)</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                <button className="btn btn-primary px-4" onClick={openAddPointModal}>
                  <i className="uil-plus me-1"></i> Add New Rate
                </button>
              </div>
            </div>

            {currentRate && (
              <div className="alert alert-info border-info d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="success" className="px-2 py-1">Active Now</Badge>
                  <span className="fw-bold fs-5">
                    1 Point = ৳{parseFloat(currentRate.bdt_per_point).toLocaleString()} BDT
                  </span>
                </div>
                <div className="small text-muted">
                  Effective since: {new Date(currentRate.effective_from).toLocaleString()}
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="py-3">#</th>
                    <th className="py-3">Ratio (1 Pt = ?)</th>
                    <th className="py-3">Company / Scope</th>
                    <th className="py-3">Effective From</th>
                    <th className="py-3">Status</th>
                    <th className="py-3">Notes</th>
                    <th className="py-3">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {pointRateList?.length > 0 ? (
                    pointRateList.map((item, index) => {
                      const status = getRateStatus(item);
                      return (
                        <tr key={item.id} className={status === "Active" ? "table-success" : ""}>
                          <td className="fw-bold">{index + 1}</td>
                          <td className="fw-bold text-primary fs-6">৳{parseFloat(item.bdt_per_point).toLocaleString()}</td>
                          <td>{item.company?.name || "Global (all companies)"}</td>
                          <td>{new Date(item.effective_from).toLocaleString()}</td>
                          <td>
                            <Badge bg={status === "Active" ? "success" : status === "Scheduled" ? "primary" : "secondary"} className="px-2">
                              {status}
                            </Badge>
                          </td>
                          <td className="small text-muted">{item.notes || "—"}</td>
                          <td className="small">{new Date(item.created_at).toLocaleDateString()}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-5">
                        No point exchange rates found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Modal show={showPointModal} onHide={() => setShowPointModal(false)} centered>
            <Modal.Header closeButton className="bg-light">
              <Modal.Title>Add Point Exchange Rate</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <form onSubmit={handlePointSubmit}>
                <div className="mb-4">
                  <label className="form-label fw-semibold">Company Scope</label>
                  <select
                    className="form-select"
                    name="company_id"
                    value={pointFormData.company_id}
                    onChange={handlePointInputChange}
                  >
                    <option value="">Global (all companies)</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  <div className="form-text mt-2">
                    Leave global to use this rate for companies without an override.
                  </div>
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold">BDT per 1 Point *</label>
                  <div className="input-group input-group-lg">
                    <span className="input-group-text bg-white">৳</span>
                    <input 
                      type="number" 
                      className="form-control" 
                      name="bdt_per_point" 
                      value={pointFormData.bdt_per_point} 
                      onChange={handlePointInputChange} 
                      placeholder="e.g. 100" 
                      min="0.01" 
                      step="0.01" 
                      required 
                    />
                  </div>
                  <div className="form-text mt-2">Enter how many BDT equals 1 system point.</div>
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold">Effective From *</label>
                  <input 
                    type="datetime-local" 
                    className="form-control" 
                    name="effective_from" 
                    value={pointFormData.effective_from} 
                    onChange={handlePointInputChange} 
                    required 
                  />
                  <div className="form-text mt-2">Set the date and time when this rate will automatically become active.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Notes</label>
                  <textarea 
                    className="form-control" 
                    name="notes" 
                    value={pointFormData.notes} 
                    onChange={handlePointInputChange} 
                    rows="3" 
                    placeholder="Briefly explain the reason for this rate change..."
                  ></textarea>
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="bg-light border-0">
              <Button variant="outline-secondary" className="px-4" onClick={() => setShowPointModal(false)}>Cancel</Button>
              <Button variant="primary" className="px-4" onClick={handlePointSubmit}>Save New Rate</Button>
            </Modal.Footer>
          </Modal>

        </div>
      </div>
    </div>
  );
};

export default PointExchangeRatePage;
